import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { notificationsAPI } from '../services/api';
import { useToast } from '../hooks/use-toast';
import { Bell, CheckCheck, Trash2, User, FileText, DollarSign, Loader2, Archive, Mail, MailOpen } from 'lucide-react';
import { format } from 'date-fns';

const Notifications = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);
  const [activeTab, setActiveTab] = useState('all');
  const [archivedIds, setArchivedIds] = useState(() => {
    // Load archived IDs from localStorage
    const saved = localStorage.getItem('archivedNotifications');
    return saved ? JSON.parse(saved) : [];
  });

  useEffect(() => {
    fetchNotifications();
  }, []);

  useEffect(() => {
    // Save archived IDs to localStorage
    localStorage.setItem('archivedNotifications', JSON.stringify(archivedIds));
  }, [archivedIds]);

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const response = await notificationsAPI.getAll();
      setNotifications(response.data.notifications || []);
      setUnreadCount(response.data.unread_count || 0);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsRead = async (id) => {
    try {
      await notificationsAPI.markAsRead(id);
      fetchNotifications();
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const handleMarkAsUnread = async (id) => {
    try {
      await notificationsAPI.markAsUnread(id);
      toast({ title: 'Success', description: 'Marked as unread' });
      fetchNotifications();
    } catch (error) {
      console.error('Error marking notification as unread:', error);
      toast({ title: 'Error', description: 'Failed to mark as unread', variant: 'destructive' });
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await notificationsAPI.markAllAsRead();
      fetchNotifications();
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };

  const handleArchive = (id) => {
    setArchivedIds(prev => [...prev, id]);
    toast({ title: 'Archived', description: 'Notification archived' });
  };

  const handleUnarchive = (id) => {
    setArchivedIds(prev => prev.filter(i => i !== id));
    toast({ title: 'Unarchived', description: 'Notification restored' });
  };

  const handleDelete = async (id) => {
    try {
      await notificationsAPI.delete(id);
      setArchivedIds(prev => prev.filter(i => i !== id));
      fetchNotifications();
    } catch (error) {
      console.error('Error deleting notification:', error);
    }
  };

  const handleNotificationClick = (notification) => {
    if (!notification.isRead) {
      handleMarkAsRead(notification._id);
    }
    if (notification.clientId) {
      navigate(`/clients/${notification.clientId}`);
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'new_client':
        return <User className="h-5 w-5" />;
      case 'new_note':
        return <FileText className="h-5 w-5" />;
      case 'new_invoice':
        return <DollarSign className="h-5 w-5" />;
      default:
        return <Bell className="h-5 w-5" />;
    }
  };

  const getNotificationColor = (type) => {
    switch (type) {
      case 'new_client':
        return 'bg-blue-50 text-blue-600';
      case 'new_note':
        return 'bg-green-50 text-green-600';
      case 'new_invoice':
        return 'bg-purple-50 text-purple-600';
      default:
        return 'bg-gray-50 text-gray-600';
    }
  };

  const activeNotifications = notifications.filter(n => !archivedIds.includes(n._id));
  const archivedNotifications = notifications.filter(n => archivedIds.includes(n._id));

  const NotificationCard = ({ notification, isArchived = false }) => (
    <Card
      key={notification._id}
      className={`cursor-pointer transition-all hover:shadow-md ${
        !notification.isRead && !isArchived ? 'border-l-4 border-l-blue-600 bg-blue-50/30' : ''
      }`}
      onClick={() => !isArchived && handleNotificationClick(notification)}
    >
      <CardContent className="p-4">
        <div className="flex items-start gap-4">
          <div className={`p-3 rounded-lg ${getNotificationColor(notification.type)}`}>
            {getNotificationIcon(notification.type)}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900 mb-1">
                  {notification.title}
                </h3>
                <p className="text-sm text-gray-600">{notification.message}</p>
                <p className="text-xs text-gray-400 mt-2">
                  {format(new Date(notification.createdAt), 'MMM dd, yyyy h:mm a')}
                </p>
              </div>
              <div className="flex items-center gap-1">
                {!isArchived && !notification.isRead && (
                  <Badge className="bg-blue-600 text-white text-xs">New</Badge>
                )}
                {!isArchived && notification.isRead && (
                  <Button
                    variant="ghost"
                    size="icon"
                    title="Mark as unread"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleMarkAsUnread(notification._id);
                    }}
                  >
                    <MailOpen className="h-4 w-4 text-gray-400 hover:text-blue-600" />
                  </Button>
                )}
                {!isArchived && (
                  <Button
                    variant="ghost"
                    size="icon"
                    title="Archive"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleArchive(notification._id);
                    }}
                  >
                    <Archive className="h-4 w-4 text-gray-400 hover:text-orange-600" />
                  </Button>
                )}
                {isArchived && (
                  <Button
                    variant="ghost"
                    size="icon"
                    title="Unarchive"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleUnarchive(notification._id);
                    }}
                  >
                    <Mail className="h-4 w-4 text-gray-400 hover:text-blue-600" />
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="icon"
                  title="Delete"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDelete(notification._id);
                  }}
                >
                  <Trash2 className="h-4 w-4 text-gray-400 hover:text-red-600" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
            Notifications
            {unreadCount > 0 && (
              <Badge className="bg-red-600 text-white">{unreadCount} new</Badge>
            )}
          </h1>
          <p className="text-gray-500 mt-1">Stay updated with your CRM activities</p>
        </div>
        {unreadCount > 0 && (
          <Button onClick={handleMarkAllAsRead} variant="outline" className="gap-2">
            <CheckCheck className="h-4 w-4" />
            Mark All as Read
          </Button>
        )}
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="all" className="gap-2">
            <Mail className="h-4 w-4" />
            Inbox ({activeNotifications.length})
          </TabsTrigger>
          <TabsTrigger value="archived" className="gap-2">
            <Archive className="h-4 w-4" />
            Archived ({archivedNotifications.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="all" className="mt-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
            </div>
          ) : (
            <div className="space-y-3">
              {activeNotifications.map((notification) => (
                <NotificationCard key={notification._id} notification={notification} />
              ))}
              {activeNotifications.length === 0 && (
                <Card>
                  <CardContent className="p-12 text-center">
                    <Bell className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-1">No notifications</h3>
                    <p className="text-gray-500">You're all caught up!</p>
                  </CardContent>
                </Card>
              )}
            </div>
          )}
        </TabsContent>

        <TabsContent value="archived" className="mt-4">
          <div className="space-y-3">
            {archivedNotifications.map((notification) => (
              <NotificationCard key={notification._id} notification={notification} isArchived />
            ))}
            {archivedNotifications.length === 0 && (
              <Card>
                <CardContent className="p-12 text-center">
                  <Archive className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-1">No archived notifications</h3>
                  <p className="text-gray-500">Archived notifications will appear here</p>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Notifications;
